import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  CheckIcon,
  XMarkIcon,
  ChartBarIcon,
  ArrowRightIcon,
  ServerIcon,
  DocumentTextIcon,
  BoltIcon,
  ShieldCheckIcon,
  CloudIcon,
  ClockIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import planApi from '../services/api/plan.api';

interface PricingPlan {
  id: string;
  name: string;
  tier: 'free' | 'starter' | 'pro' | 'enterprise';
  description: string;
  priceMonthly: number;
  priceAnnual: number;
  features: string[];
  pricing?: string[];
  limits: { infraHosts: number; apmHosts: number; retention: string };
  cta: string;
  ctaLink: string;
  highlighted: boolean;
}

export default function PricingPage() {
  const { t, i18n } = useTranslation('plans');
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');
  const [currency, setCurrency] = useState<'USD' | 'BRL'>(
    i18n.language?.startsWith('pt') ? 'BRL' : 'USD'
  );
  const [apiPlans, setApiPlans] = useState<any[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    planApi
      .listPublic(currency)
      .then((res) => {
        if (cancelled) return;
        const data = (res as any)?.data || res;
        setApiPlans(Array.isArray(data) ? data : null);
      })
      .catch(() => {
        if (!cancelled) setApiPlans(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currency]);

  const formatPrice = (n: number) =>
    new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(n);

  const fallbackPlans: PricingPlan[] = useMemo(() => [
    {
      id: 'free',
      name: 'Free',
      tier: 'free',
      description: 'Perfect for side projects and testing',
      priceMonthly: 0,
      priceAnnual: 0,
      features: [
        '3 Infrastructure hosts',
        '1 day data retention',
        '100K metrics/month',
        '500K logs/month',
        '100K traces/month',
        '1 uptime monitor included',
        'Cloud scans: $9.99 each',
        'Security scans: $14.99 each',
        'Community support',
      ],
      limits: {
        infraHosts: 3,
        apmHosts: 0,
        retention: '1 day',
      },
      cta: 'Start Free',
      ctaLink: '/register?plan=free',
      highlighted: false,
    },
    {
      id: 'starter',
      name: 'Starter',
      tier: 'starter',
      description: 'For small teams getting started',
      priceMonthly: 29,
      priceAnnual: 290,
      features: [
        '5 Infrastructure hosts included',
        '1 APM host included',
        '30 days data retention',
        '100 GB span ingestion',
        '5 GB log ingestion',
        '500K indexed spans',
        '1M indexed logs',
        '500 custom metrics',
        '5 uptime monitors included',
        '2 security scans/month included',
        'Cloud scans: $9.99 each',
        'Email support',
        '14-day free trial',
      ],
      pricing: [
        'Additional infra hosts: $12/host',
        'Additional APM hosts: $25/host',
        'Extra ingestion: Usage-based',
      ],
      limits: {
        infraHosts: 5,
        apmHosts: 1,
        retention: '30 days',
      },
      cta: 'Start 14-Day Trial',
      ctaLink: '/register?plan=starter',
      highlighted: true,
    },
    {
      id: 'pro',
      name: 'Professional',
      tier: 'pro',
      description: 'For growing businesses',
      priceMonthly: 99,
      priceAnnual: 990,
      features: [
        '10 Infrastructure hosts included',
        '3 APM hosts included',
        '90 days data retention',
        '250 GB span ingestion',
        '15 GB log ingestion',
        '2M indexed spans',
        '5M indexed logs',
        '2000 custom metrics',
        '20 uptime monitors included',
        '10 security scans/month included',
        '1 cloud scan/month included',
        'Priority support (24h)',
        'Advanced dashboards',
        'Custom integrations',
        '14-day free trial',
      ],
      pricing: [
        'Additional infra hosts: $10/host',
        'Additional APM hosts: $22/host',
        'Extra ingestion: Usage-based',
      ],
      limits: {
        infraHosts: 10,
        apmHosts: 3,
        retention: '90 days',
      },
      cta: 'Start 14-Day Trial',
      ctaLink: '/register?plan=professional',
      highlighted: false,
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      tier: 'enterprise',
      description: 'For large-scale operations',
      priceMonthly: 499,
      priceAnnual: 4990,
      features: [
        '30 Infrastructure hosts included',
        '10 APM hosts included',
        '365 days data retention',
        '1 TB span ingestion',
        '100 GB log ingestion',
        '10M indexed spans',
        '50M indexed logs',
        '10000 custom metrics',
        '100 uptime monitors included',
        '50 security scans/month included',
        '5 cloud scans/month included',
        'Dedicated support (24/7)',
        'Custom SLA (99.9%)',
        'SSO/SAML',
        'On-premise option',
        'Advanced security',
        '30-day free trial',
      ],
      pricing: [
        'Additional infra hosts: $8/host',
        'Additional APM hosts: $18/host',
        'Extra ingestion: Negotiable',
      ],
      limits: {
        infraHosts: 30,
        apmHosts: 10,
        retention: '365 days',
      },
      cta: 'Contact Sales',
      ctaLink: '#contact',
      highlighted: false,
    },
  ], []);

  const plans: PricingPlan[] = useMemo(() => {
    if (!apiPlans || apiPlans.length === 0) return fallbackPlans;

    return apiPlans
      .map((p, index): PricingPlan => {
        const tier = (p.tier || p.slug || '').toLowerCase() as PricingPlan['tier'];
        const features: string[] = Array.isArray(p.features) ? p.features : [];
        const pricing: string[] = [];

        const fmt = (n: any) =>
          new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
            style: 'currency',
            currency,
          }).format(Number(n) || 0);

        if (Number(p.price_per_infra_host) > 0)
          pricing.push(`${t('extraInfraHost', 'Additional infra hosts')}: ${fmt(p.price_per_infra_host)}`);
        if (Number(p.price_per_apm_host) > 0)
          pricing.push(`${t('extraApmHost', 'Additional APM hosts')}: ${fmt(p.price_per_apm_host)}`);
        if (Number(p.price_per_security_scan) > 0)
          pricing.push(`${t('extraSecurityScan', 'Security scans')}: ${fmt(p.price_per_security_scan)}`);

        const ctaLink =
          tier === 'enterprise' || tier === 'free'
            ? tier === 'enterprise'
              ? '#contact'
              : `/register?plan=${p.slug}`
            : `/register?plan=${p.slug}`;

        return {
          id: p.id || p.slug,
          name: p.name,
          tier: (tier || 'starter') as PricingPlan['tier'],
          description: p.description || '',
          priceMonthly: Number(p.price_monthly) || 0,
          priceAnnual: Number(p.price_annual) || 0,
          features,
          pricing: pricing.length ? pricing : undefined,
          limits: {
            infraHosts: Number(p.included_infra_hosts) || 0,
            apmHosts: Number(p.included_apm_hosts) || 0,
            retention: `${p.data_retention_days || 0} days`,
          },
          cta:
            tier === 'enterprise'
              ? t('contactSales', 'Contact Sales')
              : tier === 'free'
              ? t('startFree', 'Start Free')
              : t('startTrial', 'Start 14-Day Trial'),
          ctaLink,
          highlighted: index === 1,
        };
      })
      .sort((a, b) => a.priceMonthly - b.priceMonthly);
  }, [apiPlans, currency, fallbackPlans, t]);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Navigation */}
      <nav className="fixed w-full bg-white/80 dark:bg-gray-900/80 backdrop-blur-md z-50 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <ChartBarIcon className="h-8 w-8 text-indigo-600" />
              <span className="text-xl font-bold text-gray-900 dark:text-white">Kubmix Flux</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                Back to Home
              </Link>
              <Link
                to="/login"
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-400 mb-8">
              Pay only for what you use. All plans include a free trial.
            </p>

            {/* Billing & Currency Toggles */}
            <div className="inline-flex flex-wrap items-center justify-center gap-3">
              <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 p-1 bg-gray-50 dark:bg-gray-800">
                <button
                  onClick={() => setBillingCycle('monthly')}
                  className={`px-6 py-2 text-sm font-medium rounded-md transition-colors ${
                    billingCycle === 'monthly'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {t('monthly', 'Monthly')}
                </button>
                <button
                  onClick={() => setBillingCycle('annual')}
                  className={`px-6 py-2 text-sm font-medium rounded-md transition-colors ${
                    billingCycle === 'annual'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {t('annual', 'Annual')}
                  <span className="ml-2 text-xs text-indigo-600 dark:text-indigo-400 font-semibold">
                    {t('save17', 'Save 17%')}
                  </span>
                </button>
              </div>

              <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 p-1 bg-gray-50 dark:bg-gray-800">
                {(['USD', 'BRL'] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setCurrency(c)}
                    className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                      currency === c
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Pricing Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {plans.map((plan) => {
              const price = billingCycle === 'monthly' ? plan.priceMonthly : plan.priceAnnual / 12;
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl border-2 bg-white dark:bg-gray-800 p-8 ${
                    plan.highlighted
                      ? 'border-indigo-600 shadow-xl scale-105'
                      : 'border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {plan.highlighted && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="px-4 py-1 bg-indigo-600 text-white text-sm font-semibold rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}

                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">{plan.description}</p>
                    <div className="mb-2">
                      <span className="text-5xl font-bold text-gray-900 dark:text-white">
                        {formatPrice(price)}
                      </span>
                      {plan.priceMonthly > 0 && (
                        <span className="text-gray-600 dark:text-gray-400 text-lg">{t('perMonth', '/month')}</span>
                      )}
                    </div>
                    {billingCycle === 'annual' && plan.priceAnnual > 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {formatPrice(plan.priceAnnual)} {t('billedAnnually', '/year (billed annually)')}
                      </p>
                    )}
                  </div>

                  <Link
                    to={
                      // Propaga o billingCycle pro register: ?plan=X&billing=yearly|monthly
                      // Sem isso, user trocava pra "anual" no pricing mas chegava no
                      // register com o toggle em mensal de novo. Anchor links (#contact)
                      // ficam intactos.
                      plan.ctaLink.startsWith('/register')
                        ? `${plan.ctaLink}${plan.ctaLink.includes('?') ? '&' : '?'}billing=${billingCycle === 'annual' ? 'yearly' : 'monthly'}`
                        : plan.ctaLink
                    }
                    className={`block w-full text-center px-6 py-3 rounded-lg font-semibold mb-6 transition-colors ${
                      plan.highlighted
                        ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {plan.cta}
                  </Link>

                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm">
                        <CheckIcon className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {plan.pricing && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Usage Pricing:</p>
                      <ul className="space-y-1">
                        {plan.pricing.map((item, idx) => (
                          <li key={idx} className="text-xs text-gray-600 dark:text-gray-400">
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Usage-Based Pricing Details */}
          <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              Usage-Based Pricing Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <PricingDetail
                icon={<ServerIcon className="h-8 w-8 text-indigo-600" />}
                title="Infrastructure Monitoring"
                description="$8-$12 per host/month beyond included amount. Billed hourly based on average active hosts."
              />
              <PricingDetail
                icon={<BoltIcon className="h-8 w-8 text-purple-600" />}
                title="APM Hosts"
                description="$18-$25 per APM host/month beyond included amount. Track application performance."
              />
              <PricingDetail
                icon={<ChartBarIcon className="h-8 w-8 text-blue-600" />}
                title="Span Ingestion"
                description="$0.10 per GB beyond included amount. Distributed tracing data ingestion."
              />
              <PricingDetail
                icon={<DocumentTextIcon className="h-8 w-8 text-green-600" />}
                title="Log Ingestion"
                description="$0.05 per GB beyond included amount. Centralized log management."
              />
              <PricingDetail
                icon={<ChartBarIcon className="h-8 w-8 text-yellow-600" />}
                title="Indexed Events"
                description="$0.10 per million indexed events. Full-text search across spans and logs."
              />
              <PricingDetail
                icon={<BoltIcon className="h-8 w-8 text-red-600" />}
                title="Custom Metrics"
                description="$0.05 per 100 custom metrics beyond included amount."
              />
              <PricingDetail
                icon={<ShieldCheckIcon className="h-8 w-8 text-indigo-600" />}
                title="Cloud Security Scans"
                description="$9.99 per scan. Professional: 1/month included. Enterprise: 5/month included."
              />
              <PricingDetail
                icon={<ClockIcon className="h-8 w-8 text-teal-600" />}
                title="Uptime Monitors"
                description="$4.99 per monitor/month. Free: 1, Starter: 5, Pro: 20, Enterprise: 100 included."
              />
              <PricingDetail
                icon={<MagnifyingGlassIcon className="h-8 w-8 text-red-600" />}
                title="Security Scans"
                description="$14.99 per scan. Starter: 2/month, Pro: 10/month, Enterprise: 50/month included."
              />
            </div>
          </div>

          {/* FAQ */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <FAQItem
                question="How does the free trial work?"
                answer="All paid plans include a 14-30 day free trial. No credit card required to start. You can cancel anytime during the trial."
              />
              <FAQItem
                question="Can I change plans later?"
                answer="Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately with prorated billing."
              />
              <FAQItem
                question="What happens if I exceed my limits?"
                answer="You'll only be charged for usage beyond your plan's included amounts. Usage-based pricing is transparent and billed monthly."
              />
              <FAQItem
                question="Do you offer annual discounts?"
                answer="Yes! Annual plans save you 17% compared to monthly billing. Pay upfront and get two months free."
              />
              <FAQItem
                question="What payment methods do you accept?"
                answer="We accept all major credit cards (Visa, MasterCard, Amex, Discover) via Stripe. Enterprise plans can pay via invoice."
              />
              <FAQItem
                question="Is there a setup fee?"
                answer="No setup fees, ever. You only pay for what you use. Start monitoring in minutes with our quick setup guide."
              />
            </div>
          </div>

          {/* CTA */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to get started?
            </h2>
            <p className="text-xl text-indigo-100 mb-8">
              Start your free trial today. No credit card required.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/register"
                className="px-8 py-4 bg-white text-indigo-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg inline-flex items-center justify-center gap-2"
              >
                Start Free Trial
                <ArrowRightIcon className="h-5 w-5" />
              </Link>
              <a
                href="#contact"
                className="px-8 py-4 bg-indigo-700 text-white rounded-lg hover:bg-indigo-800 transition-colors font-semibold text-lg border-2 border-indigo-400"
              >
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-gray-900 text-gray-400 text-center">
        <p>&copy; 2025 Kubmix Flux. All rights reserved.</p>
      </footer>
    </div>
  );
}

function PricingDetail({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0">{icon}</div>
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{title}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }: { question: string; answer: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 border border-gray-200 dark:border-gray-700">
      <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{question}</h3>
      <p className="text-gray-600 dark:text-gray-400">{answer}</p>
    </div>
  );
}
